import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'

const isBold = format => typeof format === 'number' && (format & 1) === 1

const BLOCK_TYPES = ['paragraph', 'listitem', 'quote', 'heading']

// Walk a Lexical node tree and emit an ordered list of inline segments and breaks.
function collectSegments (nodes, out, ctx = {}) {
  if (!Array.isArray(nodes)) return
  for (const node of nodes) {
    if (!node) continue
    const type = node.type
    if (type === 'linebreak') {
      out.push({ br: true })
      continue
    }
    if (BLOCK_TYPES.includes(type)) {
      const forceBold = ctx.forceBold || type === 'heading'
      if (Array.isArray(node.children)) collectSegments(node.children, out, { ...ctx, forceBold })
      out.push({ br: true })
      continue
    }
    if (Array.isArray(node.children)) {
      collectSegments(node.children, out, ctx)
      continue
    }
    if (typeof node.text === 'string') {
      out.push({ text: node.text, bold: isBold(node.format) || !!ctx.forceBold })
    }
  }
}

function toLines (segments) {
  const lines = []
  let current = []
  for (const seg of segments) {
    if (seg.br) {
      lines.push(current)
      current = []
      continue
    }
    current.push(seg)
  }
  lines.push(current)
  return lines
}

// Turn the stored Lexical JSON into presentational blocks. Falls back to plain
// text when the content is not Lexical JSON.
export function parseDetails (details) {
  let root = details
  if (typeof details === 'string') {
    try {
      root = JSON.parse(details)
    } catch {
      return { blocks: [], plain: details }
    }
  }
  if (!root || typeof root !== 'object') {
    return { blocks: [], plain: typeof details === 'string' ? details : '' }
  }

  const segments = []
  collectSegments(root.root?.children || root.children || [], segments)

  const blocks = []
  for (const line of toLines(segments)) {
    const text = line.map(seg => seg.text).join('').trim()
    if (!text) continue
    const bold = line.some(seg => seg.bold && seg.text.trim())
    if (/^[•·▪◦\-*]\s+/.test(text)) {
      blocks.push({ type: 'bullet', text: text.replace(/^[•·▪◦\-*]\s+/, '') })
    } else if (bold) {
      blocks.push({ type: 'heading', text })
    } else {
      blocks.push({ type: 'paragraph', text })
    }
  }

  // A trailing bold line reads as a closing statement rather than a section.
  const last = blocks[blocks.length - 1]
  if (last && last.type === 'heading') last.type = 'closing'

  return { blocks, plain: '' }
}

export default function EventDetails ({ content }) {
  const { blocks, plain } = useMemo(() => parseDetails(content), [content])

  if (plain) {
    return (
      <Typography variant='body1' sx={{ color: 'text.secondary', lineHeight: 1.8, whiteSpace: 'pre-line' }}>
        {plain}
      </Typography>
    )
  }

  if (!blocks.length) return null

  return (
    <Box>
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          return (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: index === 0 ? 0 : 3.5, mb: 1.5 }}
            >
              <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: 'primary.main', flexShrink: 0 }} />
              <Typography variant='h6' component='h3' sx={{ fontWeight: 700, fontSize: { xs: '1.05rem', md: '1.15rem' } }}>
                {block.text}
              </Typography>
            </Box>
          )
        }
        if (block.type === 'bullet') {
          return (
            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 0.75 }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'primary.main', mt: '3px', flexShrink: 0 }} />
              <Typography variant='body1' sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                {block.text}
              </Typography>
            </Box>
          )
        }
        if (block.type === 'closing') {
          return (
            <Typography
              key={index}
              sx={{
                mt: 4,
                fontStyle: 'italic',
                fontWeight: 600,
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                color: 'primary.main',
                lineHeight: 1.6
              }}
            >
              {block.text}
            </Typography>
          )
        }
        return (
          <Typography key={index} variant='body1' sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 1.5 }}>
            {block.text}
          </Typography>
        )
      })}
    </Box>
  )
}
